import { useState, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Save,
  Eye,
  Loader2,
  Smartphone,
  Monitor,
  Undo2,
  Redo2,
} from "lucide-react";

import BlockPalette from "./BlockPalette";
import EmailCanvas from "./EmailCanvas";
import BlockEditor from "./BlockEditor";
import { generateEmailHtml } from "./htmlGenerator";
import { createDefaultTemplate, createBlock } from "./templateUtils";

const EmailBuilder = ({ isOpen, onClose, template = null, onSave }) => {
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [previewText, setPreviewText] = useState(template?.preview_text || "");
  const [category, setCategory] = useState(template?.category || "newsletter");
  const [saving, setSaving] = useState(false);
  
  // Template state
  const [sections, setSections] = useState(() => {
    if (template?.design_json?.sections) {
      return template.design_json.sections;
    }
    return createDefaultTemplate();
  });
  
  // Editor state
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [showPreview, setShowPreview] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Global styles
  const [globalStyles, setGlobalStyles] = useState(
    template?.design_json?.globalStyles || {
      backgroundColor: "#f4f4f5",
      contentBackgroundColor: "#ffffff",
      fontFamily: "Arial, sans-serif",
      primaryColor: "#1CBCF8",
      textColor: "#1f2937",
      linkColor: "#1CBCF8",
      contentWidth: "600px",
    }
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save to history for undo/redo
  const saveToHistory = useCallback((newSections) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newSections));
    setHistory(newHistory.slice(-20)); // Keep last 20 states
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSections(JSON.parse(history[historyIndex - 1]));
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSections(JSON.parse(history[historyIndex + 1]));
    }
  };

  // Handle drag events
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // If dragging from palette (new block)
    if (active.id.startsWith("palette-")) {
      const blockType = active.id.replace("palette-", "");
      const targetSectionId = over.data.current?.sectionId;
      
      if (targetSectionId) {
        const newBlock = createBlock(blockType);
        setSections((prev) => {
          const newSections = prev.map((section) => {
            if (section.id === targetSectionId) {
              return {
                ...section,
                blocks: [...section.blocks, newBlock],
              };
            }
            return section;
          });
          saveToHistory(newSections);
          return newSections;
        });
      }
      return;
    }

    // Reordering sections
    if (active.data.current?.type === "section" && over.data.current?.type === "section") {
      setSections((prev) => {
        const oldIndex = prev.findIndex((s) => s.id === active.id);
        const newIndex = prev.findIndex((s) => s.id === over.id);
        const newSections = arrayMove(prev, oldIndex, newIndex);
        saveToHistory(newSections);
        return newSections;
      });
      return;
    }

    // Reordering blocks within a section
    if (active.data.current?.type === "block" && over.data.current?.type === "block") {
      const activeSectionId = active.data.current.sectionId;
      const overSectionId = over.data.current.sectionId;

      setSections((prev) => {
        const newSections = [...prev];
        
        if (activeSectionId === overSectionId) {
          // Same section reorder
          const sectionIndex = newSections.findIndex((s) => s.id === activeSectionId);
          const section = newSections[sectionIndex];
          const oldIndex = section.blocks.findIndex((b) => b.id === active.id);
          const newIndex = section.blocks.findIndex((b) => b.id === over.id);
          newSections[sectionIndex] = {
            ...section,
            blocks: arrayMove(section.blocks, oldIndex, newIndex),
          };
        } else {
          // Moving between sections
          const fromSectionIndex = newSections.findIndex((s) => s.id === activeSectionId);
          const toSectionIndex = newSections.findIndex((s) => s.id === overSectionId);
          const block = newSections[fromSectionIndex].blocks.find((b) => b.id === active.id);
          
          newSections[fromSectionIndex] = {
            ...newSections[fromSectionIndex],
            blocks: newSections[fromSectionIndex].blocks.filter((b) => b.id !== active.id),
          };
          
          const toIndex = newSections[toSectionIndex].blocks.findIndex((b) => b.id === over.id);
          newSections[toSectionIndex] = {
            ...newSections[toSectionIndex],
            blocks: [
              ...newSections[toSectionIndex].blocks.slice(0, toIndex),
              block,
              ...newSections[toSectionIndex].blocks.slice(toIndex),
            ],
          };
        }
        
        saveToHistory(newSections);
        return newSections;
      });
    }
  };

  // Block operations
  const updateBlock = (sectionId, blockId, updates) => {
    setSections((prev) => {
      const newSections = prev.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            blocks: section.blocks.map((block) =>
              block.id === blockId ? { ...block, ...updates } : block
            ),
          };
        }
        return section;
      });
      return newSections;
    });
  };

  const deleteBlock = (sectionId, blockId) => {
    setSections((prev) => {
      const newSections = prev.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            blocks: section.blocks.filter((b) => b.id !== blockId),
          };
        }
        return section;
      });
      saveToHistory(newSections);
      return newSections;
    });
    setSelectedBlockId(null);
  };

  const duplicateBlock = (sectionId, blockId) => {
    setSections((prev) => {
      const newSections = prev.map((section) => {
        if (section.id === sectionId) {
          const blockIndex = section.blocks.findIndex((b) => b.id === blockId);
          const block = section.blocks[blockIndex];
          const newBlock = {
            ...JSON.parse(JSON.stringify(block)),
            id: `block-${Date.now()}`,
          };
          const newBlocks = [...section.blocks];
          newBlocks.splice(blockIndex + 1, 0, newBlock);
          return { ...section, blocks: newBlocks };
        }
        return section;
      });
      saveToHistory(newSections);
      return newSections;
    });
  };

  // Section operations
  const addSection = (type = "content") => {
    const newSection = {
      id: `section-${Date.now()}`,
      type,
      columns: 1,
      backgroundColor: "#ffffff",
      padding: "20px",
      blocks: [],
    };
    setSections((prev) => {
      const newSections = [...prev, newSection];
      saveToHistory(newSections);
      return newSections;
    });
  };

  const updateSection = (sectionId, updates) => {
    setSections((prev) => {
      const newSections = prev.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      );
      return newSections;
    });
  };

  const deleteSection = (sectionId) => {
    setSections((prev) => {
      const newSections = prev.filter((s) => s.id !== sectionId);
      saveToHistory(newSections);
      return newSections;
    });
  };

  // Get selected block data
  const getSelectedBlock = () => {
    for (const section of sections) {
      const block = section.blocks.find((b) => b.id === selectedBlockId);
      if (block) return { block, sectionId: section.id };
    }
    return null;
  };

  // Save template
  const handleSave = async () => {
    if (!name.trim()) {
      alert("Please enter a template name");
      return;
    }
    if (!subject.trim()) {
      alert("Please enter a subject line");
      return;
    }

    setSaving(true);
    try {
      const html = generateEmailHtml(sections, globalStyles);
      await onSave({
        name: name.trim(),
        subject: subject.trim(),
        preview_text: previewText.trim(),
        category,
        html_content: html,
        design_json: { sections, globalStyles },
      });
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const selectedBlockData = getSelectedBlock();

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-card max-w-[98vw] w-[1600px] h-[95vh] flex flex-col p-0 gap-0">
          {/* Header */}
          <DialogHeader className="px-4 py-3 border-b border-border/50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <DialogTitle className="text-lg">
                  {template ? "Edit Template" : "Create Email Template"}
                </DialogTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={undo}
                    disabled={historyIndex <= 0}
                    title="Undo"
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={redo}
                    disabled={historyIndex >= history.length - 1}
                    title="Redo"
                  >
                    <Redo2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center border rounded-md">
                  <Button
                    variant={previewMode === "desktop" ? "secondary" : "ghost"}
                    size="sm"
                    className="rounded-r-none"
                    onClick={() => setPreviewMode("desktop")}
                  >
                    <Monitor className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewMode === "mobile" ? "secondary" : "ghost"}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setPreviewMode("mobile")}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save Template
                </Button>
              </div>
            </div>
          </DialogHeader>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex flex-1 min-h-0">
              {/* Left Sidebar - Block Palette */}
              <div className="w-64 border-r border-border/50 flex flex-col bg-muted/30">
                <div className="p-3 border-b border-border/50">
                  <h3 className="font-medium text-sm">Content Blocks</h3>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  <BlockPalette />
                </div>
              </div>

              {/* Center - Canvas */}
              <div className="flex-1 flex flex-col min-w-0 bg-muted/50">
                <div className="flex-1 overflow-y-auto p-6">
                  <div
                    className="mx-auto transition-all duration-200"
                    style={{
                      maxWidth: previewMode === "mobile" ? "375px" : globalStyles.contentWidth,
                    }}
                  >
                    <EmailCanvas
                      sections={sections}
                      globalStyles={globalStyles}
                      selectedBlockId={selectedBlockId}
                      onSelectBlock={setSelectedBlockId}
                      onUpdateBlock={updateBlock}
                      onDeleteBlock={deleteBlock}
                      onDuplicateBlock={duplicateBlock}
                      onUpdateSection={updateSection}
                      onDeleteSection={deleteSection}
                      onAddSection={addSection}
                    />
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Settings */}
              <div className="w-80 border-l border-border/50 flex flex-col bg-muted/30">
                <div className="flex-1 overflow-y-auto">
                  {selectedBlockData ? (
                    <BlockEditor
                      block={selectedBlockData.block}
                      sectionId={selectedBlockData.sectionId}
                      onUpdate={(updates) =>
                        updateBlock(selectedBlockData.sectionId, selectedBlockData.block.id, updates)
                      }
                      onDelete={() =>
                        deleteBlock(selectedBlockData.sectionId, selectedBlockData.block.id)
                      }
                      onDuplicate={() =>
                        duplicateBlock(selectedBlockData.sectionId, selectedBlockData.block.id)
                      }
                      onClose={() => setSelectedBlockId(null)}
                    />
                  ) : (
                    <div className="p-4 space-y-4">
                      <h3 className="font-medium text-sm border-b border-border/50 pb-2">
                        Template Settings
                      </h3>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Template Name *</Label>
                          <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Monthly Newsletter"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Subject Line *</Label>
                          <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="e.g., Your Monthly Update"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Preview Text</Label>
                          <Input
                            value={previewText}
                            onChange={(e) => setPreviewText(e.target.value)}
                            placeholder="Shown in inbox preview"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Category</Label>
                          <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="newsletter">Newsletter</SelectItem>
                              <SelectItem value="promotional">Promotional</SelectItem>
                              <SelectItem value="transactional">Transactional</SelectItem>
                              <SelectItem value="announcement">Announcement</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <h3 className="font-medium text-sm border-b border-border/50 pb-2 pt-4">
                        Global Styles
                      </h3>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Background Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={globalStyles.backgroundColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, backgroundColor: e.target.value })
                              }
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                            <Input
                              value={globalStyles.backgroundColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, backgroundColor: e.target.value })
                              }
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Content Background</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={globalStyles.contentBackgroundColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, contentBackgroundColor: e.target.value })
                              }
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                            <Input
                              value={globalStyles.contentBackgroundColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, contentBackgroundColor: e.target.value })
                              }
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Primary Color</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={globalStyles.primaryColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, primaryColor: e.target.value })
                              }
                              className="w-8 h-8 rounded cursor-pointer"
                            />
                            <Input
                              value={globalStyles.primaryColor}
                              onChange={(e) =>
                                setGlobalStyles({ ...globalStyles, primaryColor: e.target.value })
                              }
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                        </div>
                      </div>

                      <h3 className="font-medium text-sm border-b border-border/50 pb-2 pt-4">
                        Merge Tags
                      </h3>
                      <div className="flex flex-wrap gap-1">
                        {[
                          "{{first_name}}",
                          "{{last_name}}",
                          "{{email}}",
                          "{{company}}",
                          "{{city}}",
                          "{{state}}",
                        ].map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-primary/20 text-primary px-2 py-1 rounded cursor-pointer hover:bg-primary/30"
                            onClick={() => navigator.clipboard.writeText(tag)}
                            title="Click to copy"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Click to copy, then paste in your content
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DragOverlay>
              {activeId?.startsWith("palette-") && (
                <div className="bg-primary/20 border-2 border-primary border-dashed rounded-lg p-4 text-center text-sm">
                  {activeId.replace("palette-", "").replace("-", " ")}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-card max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b border-border/50">
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-neutral-200 p-4">
            <div
              className="mx-auto bg-white shadow-lg"
              style={{ maxWidth: globalStyles.contentWidth }}
              dangerouslySetInnerHTML={{
                __html: generateEmailHtml(sections, globalStyles),
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailBuilder;
